/**
 * Centralized z-index constants for the application.
 * 
 * All z-index values should be defined here and imported where needed.
 * This ensures consistent layering and makes it easy to adjust z-index values
 * when needed.
 * 
 * Z-index hierarchy (from lowest to highest, with 100-unit gaps):
 * - Background/Base: 0-100
 * - Header and Footer: 101-200
 * - Navigation Buttons: 201-300
 * - Popups/Dialogs: 301-400
 * - Chat: 401-500
 * - Hamburger Menu/Header Menus: 501-600
 * - Other overlays: 601-700
 * - Snackbar/Toasts: 10000 (above all dialogs/menus so messages show even with modal open)
 * - Force Update Modal: 801+ (highest - blocks everything)
 */

export const Z_INDEX = {
  // Background and Base Layers (0-100) - minimum 10 difference
  BACKGROUND: 0,
  BASE: 10,
  BASE_LAYER_2: 20,
  BASE_LAYER_5: 30,
  BOTTOM_SHEET: 40,
  IMAGE_UPLOAD_OVERLAY: 50,
  STICKY_HEADER: 60,
  
  // Header and Footer (101-200) - minimum 10 difference
  FOOTER: 110,
  HEADER: 120,
  LAYOUT_OVERLAY: 130,
  
  // Navigation Buttons (201-300) - minimum 10 difference
  FORM_NAVIGATION_BUTTON: 210,
  FORM_NAVIGATION_BUTTON_OVERLAY: 220,

  // Popups/Dialogs (301-400) - minimum 10 difference
  DIALOG: 310,
  DIALOG_BACKDROP: 320,
  DIALOG_CONTENT: 330,
  
  // Chat (401-500) - minimum 10 difference
  CHAT_ICON: 410,
  CHAT_MODAL: 420,
  /** Dialogs opened from the chat modal (e.g. staff “Message a user”) — above CHAT_MODAL + backdrop */
  CHAT_MODAL_NESTED: 430,
  
  // Hamburger Menu and Header Menus (501-600) - minimum 10 difference
  // Higher z-index = appears on top
  // Order: First layer < Second layer < Third layer
  MENU_MAIN: 510, // Main hamburger menu (first layer - lowest)
  MENU_SUB_LEVEL: 530, // Submenu level (second layer - appears above main, +20 gap)
  MENU_OVERLAY: 550, // Third level menu overlay (used in ServicesMenu - appears above submenu, +20 gap)
  MENU_THIRD_LEVEL: 570, // Third level menu (used in HeaderComponent - highest, +20 gap)
  
  // Other overlays (601-700) - minimum 10 difference
  ENVIRONMENT_BANNER: 610,

  /**
   * Dashboard onboarding: highlighted menu row while the tour callout is open.
   * Sits above the blur spotlight (see DashboardOnboardingCallout) and below the callout popper.
   */
  DASHBOARD_ONBOARDING_TOUR_TARGET: 1300,
  /** Expert setup pages (service areas / services): dialog above page onboarding spotlight/callout */
  EXPERT_SETUP_DIALOG: 1600,

  // Snackbar/Toasts - must be above all dialogs/menus so messages are visible even when a modal is open
  SNACKBAR: 10000,
  
  // Force Update Modal - Highest priority, blocks everything
  // Set to maximum safe z-index value to ensure it's always on top
  FORCE_UPDATE_MODAL: 2147483647, // Maximum safe z-index value (2^31 - 1)
  // Internal stacking within the force update modal (backdrop behind, content on top)
  FORCE_UPDATE_MODAL_BACKDROP: 0,
  FORCE_UPDATE_MODAL_CONTENT: 1,
} as const;

/**
 * Helper function to get z-index value.
 * Useful for TypeScript type safety and potential future transformations.
 */
export const getZIndex = (key: keyof typeof Z_INDEX): number => {
  return Z_INDEX[key];
};

